import { CADINU_MARK } from './cadinuMark'

/**
 * The CADINU Lock seal (Bez, 2026-10-02: "a GOLD CADINU Lock seal, the official CADINU logo, not the moon art"). A struck gold medal: a
 * serrated rim, a gold bezel, sixty ticks and an arc for the time already served (in the lock's status colour), a padlock shackle on
 * top as the index mark, and the official CADINU mark on a gold-rimmed centre. Pure: a string of SVG, no text, no random ids, so the
 * page, the list rows, the share image, the PNG and the badge all draw the same seal (the countdown label is HTML laid over it).
 */
export type SealTone = 'locked' | 'soon' | 'due' | 'released' | 'vesting'

export const SEAL_COLORS: Record<SealTone, { lit: string; glow: string; moonTint: string }> = {
  locked: { lit: '#7fd4e8', glow: '#ffcf5a', moonTint: '#ffffff' },
  soon: { lit: '#f5b453', glow: '#ffcf5a', moonTint: '#fff6e0' },
  due: { lit: '#f5b453', glow: '#ffcf5a', moonTint: '#fff1cf' },
  released: { lit: '#6f7aa6', glow: '#6f7aa6', moonTint: '#a9b4dc' },
  vesting: { lit: '#a99bff', glow: '#ffcf5a', moonTint: '#ffffff' },
}

/** gold, or the same metal tarnished to pewter once the lock is released */
const METAL = {
  gold: { hi: '#fff3c4', mid: '#f2c75c', lo: '#a8741e', edge: '#6b4a12' },
  pewter: { hi: '#e3e8f5', mid: '#a9b4dc', lo: '#5d6690', edge: '#39405e' },
}

const n2 = (n: number) => (Math.round(n * 100) / 100).toString()
const CX = 100
const CY = 100

/** The lit part of a disc for `p` in 0..1 (0 = new, 0.5 = quarter, 1 = full), kept for callers of the earlier moon seal. */
export function moonLitPath(p: number, r: number, cx = CX, cy = CY): string {
  const f = Math.min(1, Math.max(0, p))
  if (f <= 0.002) return ''
  if (f >= 0.998) return `M${n2(cx - r)},${n2(cy)} a${n2(r)},${n2(r)} 0 1 0 ${n2(r * 2)},0 a${n2(r)},${n2(r)} 0 1 0 ${n2(-r * 2)},0Z`
  const t = Math.abs(r * Math.cos(Math.PI * f))
  const sweep = f < 0.5 ? 0 : 1
  return `M${n2(cx)},${n2(cy - r)} A${n2(r)},${n2(r)} 0 0 1 ${n2(cx)},${n2(cy + r)} A${n2(t)},${n2(r)} 0 0 ${sweep} ${n2(cx)},${n2(cy - r)}Z`
}

const polar = (r: number, deg: number, cy = CY) => {
  const a = ((deg - 90) * Math.PI) / 180
  return [CX + r * Math.cos(a), cy + r * Math.sin(a)] as const
}

export type SealOptions = {
  /** time served, or the released share of a vesting lock: 0..1 */
  progress: number
  tone: SealTone
  /** prefix of the gradient ids: two seals on one page must not share ids */
  uid: string
  /** the 60-tick dial (hidden on the tiny list icon) */
  ticks?: boolean
}

/** The official mark, centred on (cx, cy) and `w` wide. */
function mark(uid: string, cx: number, cy: number, w: number): string {
  const s = w / CADINU_MARK.width
  const h = CADINU_MARK.height * s
  return `<g transform="translate(${n2(cx - w / 2)} ${n2(cy - h / 2)}) scale(${(Math.round(s * 100000) / 100000).toString()})"><g transform="${CADINU_MARK.transform}" fill="url(#${uid}-mark)"><path d="${CADINU_MARK.d}"/></g></g>`
}

export function sealSvg({ progress, tone, uid, ticks = true }: SealOptions): string {
  const p = Math.min(1, Math.max(0, Number.isFinite(progress) ? progress : 0))
  const c = SEAL_COLORS[tone]
  const m = tone === 'released' ? METAL.pewter : METAL.gold
  const parts: string[] = []
  parts.push(
    `<defs>`,
    `<radialGradient id="${uid}-halo" cx="50%" cy="50%" r="50%"><stop offset="0.55" stop-color="${c.glow}" stop-opacity="0.22"/><stop offset="1" stop-color="${c.glow}" stop-opacity="0"/></radialGradient>`,
    `<linearGradient id="${uid}-gold" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${m.hi}"/><stop offset="0.45" stop-color="${m.mid}"/><stop offset="1" stop-color="${m.lo}"/></linearGradient>`,
    `<linearGradient id="${uid}-gold2" x1="1" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${m.hi}"/><stop offset="0.5" stop-color="${m.mid}"/><stop offset="1" stop-color="${m.lo}"/></linearGradient>`,
    `<radialGradient id="${uid}-face" cx="50%" cy="38%" r="70%"><stop offset="0" stop-color="#17153a"/><stop offset="1" stop-color="#070a1d"/></radialGradient>`,
    `<linearGradient id="${uid}-mark" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${tone === 'released' ? '#8f98bd' : '#8B00FE'}"/><stop offset="1" stop-color="${tone === 'released' ? '#5d6690' : '#1F00FE'}"/></linearGradient>`,
    `</defs>`,
  )

  if (ticks) {
    // padlock shackle above the medal: the dial's index mark
    parts.push(`<path d="M86,14 V4 a14,14 0 0 1 28,0 V14" stroke="url(#${uid}-gold2)" stroke-width="6" stroke-linecap="round" fill="none"/>`)
    parts.push(`<circle cx="${CX}" cy="${CY}" r="99" fill="url(#${uid}-halo)"/>`)
    // serrated rim: 72 teeth, the struck edge of a medal
    const teeth: string[] = []
    for (let i = 0; i < 72; i++) {
      const a0 = i * 5
      const [x1, y1] = polar(93, a0 - 2.5)
      const [x2, y2] = polar(98, a0)
      const [x3, y3] = polar(93, a0 + 2.5)
      teeth.push(`M${n2(x1)},${n2(y1)} L${n2(x2)},${n2(y2)} L${n2(x3)},${n2(y3)}Z`)
    }
    parts.push(`<path d="${teeth.join('')}" fill="url(#${uid}-gold)"/>`)
    parts.push(`<circle cx="${CX}" cy="${CY}" r="93" fill="url(#${uid}-gold)" stroke="${m.edge}" stroke-width="1"/>`)
    parts.push(`<circle cx="${CX}" cy="${CY}" r="86" fill="url(#${uid}-face)" stroke="${m.hi}" stroke-opacity="0.55" stroke-width="1.2"/>`)
    // sixty ticks; a tick is lit when the time it stands for has been served
    for (let i = 0; i < 60; i++) {
      const major = i % 5 === 0
      const [x1, y1] = polar(major ? 75 : 78, i * 6)
      const [x2, y2] = polar(83, i * 6)
      const on = (i + 0.5) / 60 <= p
      parts.push(`<line x1="${n2(x1)}" y1="${n2(y1)}" x2="${n2(x2)}" y2="${n2(y2)}" stroke="${on ? c.lit : m.mid}" stroke-opacity="${on ? 1 : major ? 0.55 : 0.28}" stroke-width="${major ? 2.2 : 1.3}" stroke-linecap="round"/>`)
    }
    // the arc of time served
    const circ = 2 * Math.PI * 69
    parts.push(`<circle cx="${CX}" cy="${CY}" r="69" fill="none" stroke="${m.mid}" stroke-opacity="0.18" stroke-width="3"/>`)
    if (p > 0.002) parts.push(`<circle class="arc" cx="${CX}" cy="${CY}" r="69" fill="none" stroke="${c.lit}" stroke-width="3.5" stroke-linecap="round" stroke-dasharray="${n2(circ * p)} ${n2(circ)}" transform="rotate(-90 ${CX} ${CY})"/>`)
    // the centre: the official CADINU mark on a gold-rimmed disc, a little above centre so the time label fits under it
    const MY = 86
    parts.push(`<circle cx="${CX}" cy="${MY}" r="40" fill="url(#${uid}-gold2)"/>`)
    parts.push(`<circle cx="${CX}" cy="${MY}" r="36" fill="url(#${uid}-face)"/>`)
    parts.push(mark(uid, CX + 2, MY, 44))
  } else {
    // the small seal: a gold rim, a ring of time served, the mark
    const circ = 2 * Math.PI * 80
    parts.push(`<circle cx="${CX}" cy="${CY}" r="97" fill="url(#${uid}-gold)" stroke="${m.edge}" stroke-width="2"/>`)
    parts.push(`<circle cx="${CX}" cy="${CY}" r="88" fill="url(#${uid}-face)"/>`)
    parts.push(`<circle cx="${CX}" cy="${CY}" r="80" fill="none" stroke="${m.mid}" stroke-opacity="0.22" stroke-width="9"/>`)
    if (p > 0.002) parts.push(`<circle class="arc" cx="${CX}" cy="${CY}" r="80" fill="none" stroke="${c.lit}" stroke-width="9" stroke-linecap="round" stroke-dasharray="${n2(circ * p)} ${n2(circ)}" transform="rotate(-90 ${CX} ${CY})"/>`)
    parts.push(mark(uid, CX + 4, CY, 92))
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${ticks ? '0 -10 200 214' : '0 0 200 200'}" fill="none">${parts.join('')}</svg>`
}

/** The seal as a data URI, for an <img> (the share image, the badge). */
export const sealDataUri = (o: SealOptions) => `data:image/svg+xml;base64,${typeof Buffer !== 'undefined' ? Buffer.from(sealSvg(o)).toString('base64') : btoa(sealSvg(o))}`
